import { Box, Text, useDisclosure } from '@chakra-ui/react';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import Table from 'components/Table';
import Title from 'components/Title';
import FilterGroup from 'containers/CompanySettings/AuditLogs/FilterGroup';
import { useFetchPaginatedAuditLogs, IAuditLogSearchQuery } from 'hooks/companySettings/useFetchAuditLogs';

import ViewChangeModal from './ViewChangeModal';

const initialSearchQuery: IAuditLogSearchQuery = {
  page: 1,
  page_size: 50,
};

const AuditLogs: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [searchQuery, setSearchQuery] = useState<IAuditLogSearchQuery>(initialSearchQuery);
  const [viewAuditData, setViewAuditData] = useState<any>();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { status: auditLogStatus, data } = useFetchPaginatedAuditLogs(searchQuery);

  const columns = useMemo(
    () => [
      {
        Header: t('common:time'),
        accessor: 'created_at',
        width: 200,
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Text>{data.row.original?.created_at}</Text>
            </Box>
          );
        },
      },
      {
        Header: t('common:action'),
        accessor: 'comment',
        width: 250,
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Text>{data.row.original?.comment}</Text>
            </Box>
          );
        },
      },
      {
        Header: t('common:details'),
        accessor: 'description',
        width: 250,
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Text onClick={() => viewChange(data.row.original)}>{data.row.original?.description}</Text>
              {data.row.original?.changes && (
                <Button variant="ghost" color="primary.500" onClick={() => viewChange(data.row.original)}>
                  {t('common:viewChanges')}
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const viewChange = (auditLogData: any) => {
    const changes = auditLogData?.changes;
    const auditChanges: any = [];
    changes.forEach((change: any) => {
      auditChanges.push({
        property: change[0],
        oldValue: change[1],
        newValue: change[2],
      });
    });
    const viewData = {
      date: auditLogData?.created_at,
      description: auditLogData?.description,
      auditChanges: auditChanges,
    };
    setViewAuditData(viewData);
    onOpen();
  };

  const handleActionChange = useCallback(
    (selectedSort: ILabelValueOptions) => {
      if (selectedSort) {
        setSearchQuery({ ...searchQuery, page: 1, action: selectedSort.value });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, action: '' });
      }
    },
    [searchQuery]
  );

  const handleDateChange = useCallback(
    (selectedDates: any) => {
      const [start, end] = selectedDates;
      if (start && end) {
        const formattedStart = start ? format(start, 'P') : '';
        const formattedEnd = end ? format(end, 'P') : '';
        setSearchQuery({ ...searchQuery, page: 1, date_range: `${formattedStart},${formattedEnd}` });
      }
      if (!start && !end) {
        setSearchQuery({ ...searchQuery, page: 1, date_range: '' });
      }
    },

    [searchQuery]
  );

  // eslint-disable-next-line
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IAuditLogSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  }, []);

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton> {t('companySettings:auditLogs')}</Title>
      </Box>
      <Box mt="26px">
        <ViewChangeModal isOpen={isOpen} onClose={onClose} viewData={viewAuditData} />
        <FilterGroup
          handleDateChange={handleDateChange}
          handleActionChange={handleActionChange}
          handleSearchInputChange={handleSearchInputChange}
        />
        <Table
          columns={columns}
          data={data?.payload || []}
          paginationData={data?.meta_data?.pagination}
          onPageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          isLoading={auditLogStatus === 'loading'}
          enableRowSelect
          paginationStyle="rowsPerPage"
          emptyTableStateProps={{
            searchText: searchQuery.search,
          }}
        />
      </Box>
    </Box>
  );
};
export default AuditLogs;
